import { useCallback, useEffect, useRef, useState } from 'react';
import { getWorkflowInbox } from '../services/workflowService';
import type { WorkflowInboxFilters, WorkflowInboxItem } from '../types/workflow';
import { workflowErrorMessage } from '../utils/workflowErrors';
import { createDefaultWorkflowFilters } from '../utils/workflowFilters';

const DEFAULT_PAGE_SIZE = 20;

export function useWorkflowInbox() {
  const [items, setItems] = useState<WorkflowInboxItem[]>([]);
  const [filters, setFiltersState] = useState<WorkflowInboxFilters>(
    createDefaultWorkflowFilters(),
  );
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState<number | null>(null);

  const mounted = useRef(true);
  const requestId = useRef(0);
  const loadingMore = useRef(false);
  const pageRef = useRef(1);
  const filtersRef = useRef(filters);

  const fetchPage = useCallback(
    async (nextPage: number, mode: 'replace' | 'append' | 'refresh') => {
      const currentRequest = ++requestId.current;
      if (mode === 'append') {
        if (loadingMore.current) return;
        loadingMore.current = true;
        setIsLoadingMore(true);
      } else if (mode === 'refresh') {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      try {
        const result = await getWorkflowInbox({
          page: nextPage,
          pageSize: DEFAULT_PAGE_SIZE,
          filters: filtersRef.current,
        });

        if (!mounted.current || currentRequest !== requestId.current) return;

        setPage(result.page);
        pageRef.current = result.page;
        setHasMore(result.page < result.totalPages);
        setTotalItems(result.totalItems);
        setItems(current => {
          if (mode === 'append') {
            const seen = new Set(current.map(item => item.workflowInstanceId));
            const merged = [...current];
            for (const item of result.items) {
              if (!seen.has(item.workflowInstanceId)) merged.push(item);
            }
            return merged;
          }
          return result.items;
        });
      } catch (caught) {
        if (mounted.current && currentRequest === requestId.current) {
          setError(
            workflowErrorMessage(caught, 'Unable to load approval inbox.'),
          );
        }
      } finally {
        loadingMore.current = false;
        if (mounted.current && currentRequest === requestId.current) {
          setIsLoading(false);
          setIsRefreshing(false);
          setIsLoadingMore(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const applyFilters = useCallback(
    (next: WorkflowInboxFilters) => {
      setFiltersState(next);
      filtersRef.current = next;
      pageRef.current = 1;
      fetchPage(1, 'replace');
    },
    [fetchPage],
  );

  const resetFilters = useCallback(() => {
    const defaults = createDefaultWorkflowFilters();
    setFiltersState(defaults);
    filtersRef.current = defaults;
    pageRef.current = 1;
    fetchPage(1, 'replace');
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore.current || isLoading || isRefreshing) return;
    fetchPage(pageRef.current + 1, 'append');
  }, [fetchPage, hasMore, isLoading, isRefreshing]);

  const removeCompletedItem = useCallback((workflowInstanceId: number) => {
    setItems(current =>
      current.filter(item => item.workflowInstanceId !== workflowInstanceId),
    );
  }, []);

  const updateInboxItem = useCallback(
    (workflowInstanceId: number, patch: Partial<WorkflowInboxItem>) => {
      setItems(current =>
        current.map(item =>
          item.workflowInstanceId === workflowInstanceId
            ? { ...item, ...patch }
            : item,
        ),
      );
    },
    [],
  );

  return {
    items,
    filters,
    page,
    isLoading,
    isRefreshing,
    isLoadingMore,
    hasMore,
    error,
    totalItems,
    loadInbox: () => fetchPage(1, 'replace'),
    refreshInbox: () => fetchPage(1, 'refresh'),
    loadMore,
    applyFilters,
    resetFilters,
    retry: () => fetchPage(1, 'replace'),
    removeCompletedItem,
    updateInboxItem,
  };
}
